import cx from 'classnames'
import type React from 'react'
import {
  forwardRef,
  type PropsWithoutRef,
  type RefAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'

import { preventDefaultHandler } from '@/helpers'
import { useToggle } from '@/hooks'
import useClickOutside from '../../hooks/useClickOutside'
import { Content } from './Content'
import css from './DropDown.module.scss'
import type { DropDownRef, Props } from './types'

const DropDown: React.ForwardRefExoticComponent<
  PropsWithoutRef<Props> & RefAttributes<DropDownRef>
> = forwardRef<DropDownRef, Props>(
  (
    {
      type = 'normal',
      children,
      disabled,
      top,
      bottom,
      right,
      contentClassName,
      handleClose,
      setStatus,
      contentMinWidth,
      closeOnMouseDown,
      classnames,
      shadow = 'default',
    },
    ref
  ) => {
    const buttonRef = useRef<HTMLDivElement | null>(null)

    const [isOpen, toggleOpen, { turnOff: close, turnOn: open }] = useToggle()

    const clickOutside = useClickOutside<HTMLDivElement>(isOpen, close)

    const handleOpen = (e: React.MouseEvent) => {
      // @TODO review problem with stopPropagation and closing systemDialog
      e.stopPropagation()
      if (!disabled) {
        toggleOpen(!isOpen)
      }
    }

    useImperativeHandle(ref, () => ({ close, open }))

    useEffect(() => {
      if (!isOpen) {
        handleClose?.()
      }
      setStatus?.(isOpen)
    }, [isOpen])

    return (
      <div
        className={cx(css.container, classnames?.container)}
        {...clickOutside}
        onMouseDown={preventDefaultHandler}
      >
        <div className={css.children} ref={buttonRef} onClick={handleOpen}>
          {children[0](isOpen, toggleOpen)}
        </div>
        {isOpen && (
          <Content
            children={children}
            buttonRef={buttonRef}
            contentMinWidth={contentMinWidth}
            top={top}
            right={right}
            bottom={bottom}
            closeOnMouseDown={closeOnMouseDown}
            toggleOpen={toggleOpen}
            contentClassName={contentClassName}
            type={type}
            shadow={shadow}
          />
        )}
      </div>
    )
  }
)

export default DropDown
